/**
 * Surface Fit Tool
 *
 * Creates a SURFACE group two ways:
 * - In 3D: fit a plane to the point cloud (RANSAC, via PlaneCreator) — the
 *   monitoring-mode counterpart of the former plane tool.
 * - In 2D: draw a free line (drag, or two clicks) anywhere; the line is raycast into
 *   the point cloud to fit a vertical SECTION surface along it. No need to hit an
 *   existing edge.
 *
 * The fitted plane is only a transient preview; on finish we dispatch the existing
 * `create_surface` event so the surface is persisted as a group with its geometry
 * stored as reactive properties (handled in usePotreeEvents.onCreateSurface). Used by
 * both the Monitoring persona and the Surface persona.
 */

import { BaseTool } from '../../BaseTool';
import { Viewer } from '../../../viewer/viewer';
import { IPersona, IViewConfig } from '../../types';
import { ViewModes } from '../../helpers';
import { CameraMode } from '../../../defines';
import { PlaneCreatorWidget } from '../../../utils/DebugWidgets';
import { PointoramaPlane } from '../../../utils/PointoramaPlane';
import { PlaneCreator } from '../modules/PlaneCreator';
import { SurfaceKind } from '../../../utils/PointoramaSurface';
import { OverlayLine } from '../../../utils/OverlayLine';
import { getPointsAlongRay } from '../../../utils/PointCloudMathUtils';
import { DefaultColors } from '../../../defines';
import * as THREE from 'three';

// Number of rays to cast along the drawn line when fitting a section plane.
const LINE_RAY_COUNT = 10;
// Fallback distance from camera if no point cloud intersections are found.
const FALLBACK_PLANE_DISTANCE = 10;
// Minimum drag distance (pixels) to treat the gesture as a drawn line.
const MIN_DRAG_DISTANCE = 10;

export class SurfaceFitPersonaTool extends BaseTool {
  readonly name = 'plane';

  private _activePlane!: PointoramaPlane;
  private planeCreator!: PlaneCreator;

  private shiftDown = false;
  private lastLeftClickTime = 0;
  private readonly DOUBLE_CLICK_INTERVAL = 300;

  private previousDoubleClickZoomState = true;
  private planeDebugWidget: PlaneCreatorWidget | null = null;

  // 2D free-line drawing state (drag or two-click → section plane).
  private overlayLine: OverlayLine | null = null;
  private lineDrawStart: THREE.Vector2 | null = null;
  private lineDrawCurrent: THREE.Vector2 | null = null;
  private isDrawingLine = false;
  /** Set after a drag-drawn line commits, so the trailing click is ignored. */
  private wasDrawingLine = false;

  /** Expose activePlane so buildClipState can include the in-progress preview as a clip plane. */
  get activePlane(): PointoramaPlane {
    return this._activePlane;
  }

  private get planeScene(): THREE.Scene {
    return this.viewer.planeScene;
  }

  constructor(viewer: Viewer, persona: IPersona) {
    super(viewer, persona);

    this._activePlane = this.createActivePlane();
    this.planeCreator = new PlaneCreator(this.viewer, this._activePlane);
    this.planeScene.add(this.planeCreator);
    this.initUndoRedo();
  }

  // ─── Lifecycle ──────────────────────────────────────────────────────

  protected onEnable(): void {
    this.previousDoubleClickZoomState = this.viewer.orbitControls?.doubleClickZoomEnabled ?? true;
    if (this.viewer.orbitControls) {
      this.viewer.orbitControls.doubleClickZoomEnabled = false;
    }

    if (this.viewer.debugOverlay && !this.planeDebugWidget) {
      this.planeDebugWidget = new PlaneCreatorWidget(this.viewer);
      this.viewer.debugOverlay.addWidget(this.planeDebugWidget);
    }

    if (!this.overlayLine) {
      this.overlayLine = new OverlayLine({ color: '#' + DefaultColors.OBJECTS.toString(16).padStart(6, '0'), lineWidth: 2 });
    }
  }

  protected onDisable(): void {
    this._activePlane.visible = false;
    this._activePlane.unHighlight();
    this.planeCreator.angleLabel.visible = false;
    this.planeCreator.reset();

    this.shiftDown = false;
    this.lastLeftClickTime = 0;
    this.clearLineDrawingState();

    if (this.viewer.orbitControls) {
      this.viewer.orbitControls.doubleClickZoomEnabled = this.previousDoubleClickZoomState;
    }

    this.viewer.inputHandler.unblockOrbitControls();
    this.viewer.renderer.domElement.style.cursor = '';

    if (this.viewer.debugOverlay && this.planeDebugWidget) {
      this.viewer.debugOverlay.removeWidget(this.planeDebugWidget.name);
      this.planeDebugWidget = null;
    }

    if (this.overlayLine) {
      this.overlayLine.dispose();
      this.overlayLine = null;
    }
  }

  getViewConfig(): IViewConfig {
    return {
      // 3D for plane fitting; 2D for click-an-edge section creation.
      views: [ViewModes.PERSPECTIVE_3D_RGB, ViewModes.ORTHOGRAPHIC_2D_TOP, ViewModes.XRAY_2D],
      defaultViewId: 'perspective_3d_rgb',
    };
  }

  private is2DMode(): boolean {
    return this.viewer.scene.cameraMode === CameraMode.ORTHOGRAPHIC;
  }

  // ─── Plane preview management ──────────────────────────────────────

  private createActivePlane(): PointoramaPlane {
    const newPlane = new PointoramaPlane();
    newPlane.tolerance = 0.01;
    newPlane.visible = false;
    this.planeScene.add(newPlane);
    return newPlane;
  }

  private planeUnderConstruction(): boolean {
    return this.planeCreator.selectedFittingPoints.length > 0;
  }

  private cancel(): void {
    if (!this.planeUnderConstruction()) return;
    this._activePlane.visible = false;
    this._activePlane.unHighlight();
    this.planeCreator.angleLabel.visible = false;
    this.planeCreator.reset();
  }

  /** Dispatch create_surface for a position+normal. SURFACE in 3D, SECTION from a 2D edge. */
  private createSurface(position: THREE.Vector3, normal: THREE.Vector3, kind: SurfaceKind): void {
    this.viewer.scene.dispatchEvent({
      type: 'create_surface',
      value: {
        plane: {
          position: { x: position.x, y: position.y, z: position.z },
          normal: { x: normal.x, y: normal.y, z: normal.z },
        },
        surfaceKind: kind,
      },
    });
  }

  /** Commit the fitted 3D plane preview as a SURFACE group. */
  private finishSurface(): void {
    if (!this.planeUnderConstruction()) return;
    if (this.shiftDown) {
      this._activePlane.snapInclination();
    }

    this.createSurface(this._activePlane.position.clone(), this._activePlane.getNormal().clone(), SurfaceKind.SURFACE);

    // Reset the preview plane for the next surface
    this._activePlane.visible = false;
    this._activePlane.unHighlight();
    this._activePlane = this.createActivePlane();
    this.planeCreator.activePlane = this._activePlane;
    this.planeCreator.reset();
  }

  private update(): void {
    if (this.is2DMode()) {
      // 2D draws a free section line; no live plane preview.
      this._activePlane.visible = false;
      return;
    }
    const mouse = this.viewer.inputHandler.mouse;
    this.planeCreator.adjustActivePlaneToMousePosition(mouse);
    if (this.shiftDown) {
      this._activePlane.snapInclination();
    }
  }

  private pixelPos(event: MouseEvent): THREE.Vector2 {
    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    return new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);
  }

  /** Unproject a screen pixel onto a horizontal plane at the current view-target Z. */
  private screenToWorld2D(screenPos: THREE.Vector2): THREE.Vector3 | null {
    const camera = this.viewer.scene.getActiveCamera();
    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    const ndcX = (screenPos.x / rect.width) * 2 - 1;
    const ndcY = -(screenPos.y / rect.height) * 2 + 1;
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
    const targetZ = this.viewer.scene.view.position.z;
    const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), -targetZ);
    const intersection = new THREE.Vector3();
    return raycaster.ray.intersectPlane(plane, intersection) ? intersection : null;
  }

  /** Snap a screen-space line end to the nearest 45° from its start. */
  private snapLineTo45Degrees(start: THREE.Vector2, end: THREE.Vector2): THREE.Vector2 {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    if (length < 1) return end.clone();
    const snap = Math.PI / 4;
    const angle = Math.round(Math.atan2(dy, dx) / snap) * snap;
    return new THREE.Vector2(start.x + Math.cos(angle) * length, start.y + Math.sin(angle) * length);
  }

  private clearLineDrawingState(): void {
    this.isDrawingLine = false;
    this.lineDrawStart = null;
    this.lineDrawCurrent = null;
    this.overlayLine?.clearPoints();
  }

  /** Update the overlay line to the current draw end (with optional 45° snap). */
  private updateDrawnLine(end: THREE.Vector2): void {
    if (!this.lineDrawStart) return;
    let drawEnd = end;
    if (this.shiftDown) drawEnd = this.snapLineTo45Degrees(this.lineDrawStart, end);
    this.lineDrawCurrent = drawEnd;
    if (this.overlayLine) {
      this.overlayLine.setFromPoints([this.lineDrawStart, drawEnd]);
      this.overlayLine.draw();
    }
  }

  /**
   * Fit a vertical SECTION plane from the drawn 2D line: cast rays along it into the
   * point cloud and take the bounding-box centre as the position, with a normal
   * perpendicular to the line direction in the XY plane.
   */
  private createSectionFromDrawnLine(): void {
    if (!this.lineDrawStart || !this.lineDrawCurrent) return;

    const camera = this.viewer.scene.getActiveCamera();
    const cameraPosition = camera.position.clone();
    const rect = this.viewer.renderer.domElement.getBoundingClientRect();

    // Cast rays sampled along the drawn screen line to find point cloud hits.
    const allIntersections: THREE.Vector3[] = [];
    for (let i = 0; i < LINE_RAY_COUNT; i++) {
      const t = i / (LINE_RAY_COUNT - 1);
      const screenPoint = new THREE.Vector2(
        this.lineDrawStart.x + t * (this.lineDrawCurrent.x - this.lineDrawStart.x),
        this.lineDrawStart.y + t * (this.lineDrawCurrent.y - this.lineDrawStart.y),
      );
      const ndcX = (screenPoint.x / rect.width) * 2 - 1;
      const ndcY = -(screenPoint.y / rect.height) * 2 + 1;
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);

      for (const pointcloud of this.viewer.scene.pointclouds) {
        const hits = getPointsAlongRay(pointcloud, raycaster.ray, 1000, 0.5, false);
        if (hits && hits.length > 0) {
          let minDist = Infinity;
          let maxDist = -Infinity;
          let nearestHit: THREE.Vector3 | null = null;
          let farthestHit: THREE.Vector3 | null = null;
          for (const hit of hits) {
            const distance = cameraPosition.distanceTo(hit.position);
            if (distance < minDist) {
              minDist = distance;
              nearestHit = hit.position.clone();
            }
            if (distance > maxDist) {
              maxDist = distance;
              farthestHit = hit.position.clone();
            }
          }
          if (nearestHit && farthestHit) {
            allIntersections.push(new THREE.Vector3().addVectors(nearestHit, farthestHit).multiplyScalar(0.5));
          }
        }
      }
    }

    // Normal: perpendicular to the drawn line direction (vertical surface).
    const startWorld = this.screenToWorld2D(this.lineDrawStart);
    const endWorld = this.screenToWorld2D(this.lineDrawCurrent);
    let planeNormal: THREE.Vector3;
    if (!startWorld || !endWorld) {
      planeNormal = new THREE.Vector3(1, 0, 0);
    } else {
      const lineDir = new THREE.Vector3().subVectors(endWorld, startWorld).normalize();
      planeNormal = new THREE.Vector3(-lineDir.y, lineDir.x, 0).normalize();
    }

    // Position: bounding-box centre of the raycast hits, else a fallback in front.
    let planePosition: THREE.Vector3;
    if (allIntersections.length > 0) {
      const boundingBox = new THREE.Box3();
      for (const p of allIntersections) boundingBox.expandByPoint(p);
      planePosition = boundingBox.getCenter(new THREE.Vector3());
    } else {
      const cameraDirection = camera.getWorldDirection(new THREE.Vector3());
      planePosition = cameraPosition.clone().add(cameraDirection.multiplyScalar(FALLBACK_PLANE_DISTANCE));
    }

    // Orient the normal toward the camera.
    const toCamera = new THREE.Vector3().subVectors(cameraPosition, planePosition);
    if (planeNormal.dot(toCamera) < 0) planeNormal.negate();

    this.createSurface(planePosition, planeNormal, SurfaceKind.SECTION);
  }

  // ─── Input Handlers ────────────────────────────────────────────────

  public onMouseDown(event: MouseEvent): void {
    if (event.button !== 0 || !this.is2DMode()) return;
    // Start a free draw line (drag, or first click of a two-click line).
    if (!this.lineDrawStart) {
      this.lineDrawStart = this.pixelPos(event);
      this.lineDrawCurrent = this.lineDrawStart.clone();
    }
  }

  public onClick(_event: MouseEvent): void {
    // 2D: a non-drag click is the second point of a two-click section line.
    if (this.is2DMode()) {
      if (this.wasDrawingLine) {
        this.wasDrawingLine = false;
        return;
      }
      if (this.lineDrawStart && this.lineDrawCurrent && this.lineDrawStart.distanceTo(this.lineDrawCurrent) >= MIN_DRAG_DISTANCE) {
        this.createSectionFromDrawnLine();
      }
      this.clearLineDrawingState();
      return;
    }

    const mouse = this.viewer.inputHandler.mouse;
    const now = Date.now();

    if (now - this.lastLeftClickTime < this.DOUBLE_CLICK_INTERVAL) {
      this.lastLeftClickTime = 0;
      this.finishSurface();
      return;
    }
    this.lastLeftClickTime = now;

    const result = this.planeCreator.addMarker(mouse);
    if (result === 'finish') {
      if (this.shiftDown) this._activePlane.snapInclination();
      this.finishSurface();
    } else if (result === 'added') {
      if (this.shiftDown) this._activePlane.snapInclination();
    }
  }

  public onRightClick(_event: MouseEvent): void {
    if (this.is2DMode()) {
      this.clearLineDrawingState();
      return;
    }
    const mouse = this.viewer.inputHandler.mouse;
    this.planeCreator.removeMarker();
    this.planeCreator.adjustActivePlaneToMousePosition(mouse);
  }

  public onMouseMove(event: MouseEvent): void {
    if (this.is2DMode()) {
      // Crosshair signals that clicking/dragging here draws a section line.
      this.viewer.renderer.domElement.style.cursor = 'crosshair';
      if (this.lineDrawStart) {
        const pixel = this.pixelPos(event);
        if (this.lineDrawStart.distanceTo(pixel) >= MIN_DRAG_DISTANCE) this.isDrawingLine = true;
        this.updateDrawnLine(pixel);
        return;
      }
    }
    this.update();
  }

  public onMouseUp(_event: MouseEvent): void {
    // A drag that exceeded the threshold commits the line on release (the trailing
    // click is filtered by BaseTool as a drag, but guard with wasDrawingLine too).
    if (this.is2DMode() && this.isDrawingLine && this.lineDrawStart && this.lineDrawCurrent) {
      this.createSectionFromDrawnLine();
      this.wasDrawingLine = true;
      this.clearLineDrawingState();
    }
    this.viewer.inputHandler.unblockOrbitControls();
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.clearLineDrawingState();
      this.cancel();
      return;
    }

    if (event.key === 'Shift') {
      this.shiftDown = true;
      this.update();
    } else if (event.key === 'CapsLock') {
      this.shiftDown = event.getModifierState('CapsLock');
      this.update();
    } else if (event.key === 'Enter') {
      if (!this.planeUnderConstruction()) return;
      this.planeCreator.adjustActivePlane();
      if (this.shiftDown) this._activePlane.snapInclination();
      this.finishSurface();
    }
  }

  public onKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Shift') {
      this.shiftDown = false;
      this.update();
    }
  }
}
